import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, FileDown, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable bulk-upload dialog.
 * Props:
 *   trigger: Element (button etc.) — pass a button
 *   title: "Bulk upload items" / "Bulk upload finished products"
 *   templateUrl: relative API URL for CSV template download (e.g. "/inventory/items/template")
 *   uploadUrl: relative API URL for POST bulk (e.g. "/inventory/items/bulk")
 *   payloadKey: e.g. "items" or "products" — the key wrapping the array in POST body
 *   testId: root testid
 *   onSuccess: callback after successful upload
 */
export default function BulkUploadDialog({ trigger, title, templateUrl, uploadUrl, payloadKey, testId = "bulk-upload", onSuccess }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = (text) => {
    // simple CSV — handles quoted fields with commas
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = splitCsvLine(lines[0]).map((h) => h.trim());
    return lines.slice(1).filter(Boolean).map((line) => {
      const parts = splitCsvLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (parts[i] || "").trim(); });
      return obj;
    });
  };

  const splitCsvLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out;
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result));
        setRows(parsed);
        if (parsed.length === 0) toast.error("Could not parse any rows. Check the CSV format.");
      } catch (err) { toast.error("Could not parse the CSV file"); }
    };
    reader.readAsText(f);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateUrl.split("/").pop() + ".csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const submit = async () => {
    if (!rows.length) { toast.error("Please pick a CSV file first"); return; }
    setUploading(true); setResult(null);
    try {
      const payload = { [payloadKey]: rows };
      const { data } = await api.post(uploadUrl, payload);
      setResult(data);
      toast.success(`${data.created} created · ${data.skipped_existing} skipped · ${data.errors.length} errors`);
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => { setFile(null); setRows([]); setResult(null); };

  return (
    <>
      {trigger ? <div onClick={() => setOpen(true)} data-testid={`${testId}-trigger`} className="inline-flex">{trigger}</div>
        : <Button variant="outline" onClick={() => setOpen(true)} data-testid={`${testId}-trigger`}><Upload className="w-4 h-4 mr-1" /> Bulk upload</Button>}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> {title}</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <div className="flex items-center gap-2 mb-2">
                <FileDown className="w-4 h-4" />
                <span className="text-sm font-bold">Step 1 · Download template</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Get the CSV template with sample rows. Fill in your data, save as CSV, then upload below.</p>
              <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid={`${testId}-template-btn`}><FileDown className="w-4 h-4 mr-1" /> Download CSV template</Button>
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="w-4 h-4" />
                <span className="text-sm font-bold">Step 2 · Upload your filled CSV</span>
              </div>
              <input type="file" accept=".csv" onChange={handleFile} data-testid={`${testId}-file-input`} className="text-sm file:mr-3 file:px-3 file:py-1.5 file:border-0 file:rounded-md file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" />
              {file ? (
                <div className="mt-3 text-xs">
                  <Badge variant="outline">{file.name}</Badge>
                  <span className="ml-2 text-muted-foreground">· {rows.length} row{rows.length === 1 ? "" : "s"} parsed</span>
                </div>
              ) : null}
              {rows.length > 0 ? (
                <div className="mt-3 rounded-md border border-border overflow-x-auto max-h-40 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-muted"><tr>{Object.keys(rows[0]).map((h) => <th key={h} className="text-left px-2 py-1 font-mono">{h}</th>)}</tr></thead>
                    <tbody>
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          {Object.keys(rows[0]).map((h) => <td key={h} className="px-2 py-1">{r[h] || "—"}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rows.length > 5 ? <div className="text-center text-muted-foreground text-[10px] py-1">…+{rows.length - 5} more rows</div> : null}
                </div>
              ) : null}
            </div>

            {result ? (
              <div className="rounded-lg border border-border p-4 space-y-2" data-testid={`${testId}-result`}>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="font-bold">Upload complete</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <Badge variant="outline" className="bg-emerald-50">Created: {result.created}</Badge>
                  <Badge variant="outline" className="bg-amber-50">Skipped (already exists): {result.skipped_existing}</Badge>
                  <Badge variant="outline" className={result.errors.length ? "bg-red-50" : ""}>Errors: {result.errors.length}</Badge>
                </div>
                {result.errors.length > 0 ? (
                  <div className="rounded-md bg-red-50 border border-red-200 p-2 max-h-32 overflow-y-auto text-xs space-y-0.5">
                    {result.errors.slice(0, 20).map((er, i) => (
                      <div key={i} className="flex items-start gap-1.5"><AlertCircle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" /><span>Row {er.row} · <b>{er.sku || er.code}</b> · {er.error}</span></div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={submit} disabled={!rows.length || uploading} data-testid={`${testId}-submit-btn`}>
                <Upload className="w-4 h-4 mr-1" />
                {uploading ? "Uploading…" : `Upload ${rows.length} row${rows.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
